import type React from 'react'
import { useRef } from 'react'
import type { WrapStyle } from '@/lib/object-effects/types/wrap-style'
import { buildWrapStyleSwitch } from '../helpers/wrap-commit-entries'
import { normWrapStyle } from '../helpers/wrap-style-sides'
import type { CommitEntry, StyleOffsetMemory, WrapTarget } from '../types/wrap-offset'

type UseWrapStyleSwitchArgs = {
  targets: WrapTarget[]
  setStyleByTargetId: React.Dispatch<React.SetStateAction<Map<string, WrapStyle>>>
  commitToTargets: (entries: CommitEntry[]) => void
}

/** Returns `applyWrapStyle`, backed by the session-only per-object, per-style offset memory. */
export function useWrapStyleSwitch({
  targets,
  setStyleByTargetId,
  commitToTargets,
}: UseWrapStyleSwitchArgs) {
  /** Session-only (never sent to the backend) per-style offset memory, one entry per selected
   *  object id — see `relevantOffsetKeysForStyle`. Never reset wholesale: each object id owns its
   *  own entry for the component's lifetime, so one object's remembered values can never leak onto
   *  or overwrite another's, regardless of how the selection changes shape around it (AC 7/8's
   *  "must not become a single global value" requirement). */
  const styleOffsetMemoryByIdRef = useRef<Map<string, StyleOffsetMemory>>(new Map())
  const getMemoryFor = (id: string) => {
    let memory = styleOffsetMemoryByIdRef.current.get(id)

    if (!memory) {
      memory = {}
      styleOffsetMemoryByIdRef.current.set(id, memory)
    }

    return memory
  }

  /** Switching styles ALWAYS explicitly writes the entering style's own value for each of its
   *  relevant fields (see `relevantOffsetKeysForStyle`) — either what's remembered for it in that
   *  object's own offset memory, or the 6pt default if this object has never had that style
   *  customized before. This matters because the backend has only one shared set of four offset
   *  fields for the whole object, not one set per style — Jump and Wrap Shape both live on
   *  `wrapOffsetTop`, so without this, editing the value while on Wrap Shape would silently
   *  overwrite what Jump had, and a style visited for the first time would inherit whatever number
   *  the PREVIOUS style left behind instead of showing its own fresh 6pt default. Example: Jump
   *  Top/Bottom=9 -> Wrap Shape (never customized) must show 6, not 9; Wrap Shape set to 12 ->
   *  Jump must show 9 again, not 12. The leaving style's CURRENT values are snapshotted into
   *  memory first, so nothing is ever lost — including switching to No Wrap, which always displays
   *  the 6pt default (`relevantOffsetKeysForStyle` returns all four keys for 'none') without
   *  erasing whatever the other styles had.
   *
   *  For multiple selected targets (AC 7/8): applies `next` to every target, each against its OWN
   *  current style and OWN offset memory — a circle on "Wrap Shape" and a triangle on
   *  "Bounding box" both switching to "Jump" each restore/default independently, exactly as if
   *  each had been switched alone. One target already on `next` is left untouched (no redundant
   *  patch), so choosing the style already shared by part of a mixed selection only actually
   *  changes the objects that needed it — the mixed state still disappears from the UI because
   *  every target now resolves to the same `styleByTargetId` entry. All entries commit together in
   *  one batch (`commitToTargets`) — one undo transaction for the whole selection. */
  const applyWrapStyle = (next: WrapStyle) => {
    if (targets.length === 0) return

    const entries: CommitEntry[] = []

    for (const t of targets) {
      const before = normWrapStyle(t.obj)

      if (before === next) continue

      const memory = getMemoryFor(t.id)
      const { snapshot, entry } = buildWrapStyleSwitch(t, before, next, memory)

      memory[before] = snapshot
      entries.push(entry)
    }

    if (entries.length > 0) {
      // Optimistic, immediate local update — don't wait for the sync effect (which only re-runs
      // once `activeObjects` context itself changes) to reflect the new style in the button
      // highlight and offset-enablement state. Matches the old single-target code's immediate
      // `setWrapStyle(next)` call, generalized to a per-target map.
      setStyleByTargetId((prev) => {
        const nextMap = new Map(prev)

        for (const e of entries) nextMap.set(e.target.id, next)

        return nextMap
      })
    }

    commitToTargets(entries)
  }

  return applyWrapStyle
}
